/**
 * PlayerCardPage - Standalone player card page for shareable URLs
 *
 * Route: /care/player-card/:entityId
 *
 * This page can be accessed directly via URL for sharing player cards externally.
 * It displays the same player card content as the modal but as a full page.
 */
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router';
import {
  Box,
  Typography,
  IconButton,
  Avatar,
  Grid,
  Chip,
  Paper,
  Tabs,
  Tab,
  Container,
  CircularProgress,
  Alert,
  Tooltip,
  Snackbar,
} from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import PrintIcon from '@mui/icons-material/Print';
import PersonIcon from '@mui/icons-material/Person';
import { PLAYER_CARDS, CCM_RESOURCES, ENTITY_TYPE_CONFIG } from './demoResources';
import { TabPanel, ENTITY_ICONS, BRAND_COLORS, getInitials } from './playerCardShared';

// eslint-disable-next-line max-lines-per-function
function PlayerCardPage() {
  const { entityId } = useParams();
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [playerCardData, setPlayerCardData] = useState(null);
  const [showCopiedSnackbar, setShowCopiedSnackbar] = useState(false);

  useEffect(() => {
    // Simulate loading player card data
    // In production, this would fetch from the API
    const loadPlayerCard = async () => {
      setLoading(true);
      try {
        // Find entity in demo resources
        let foundEntity = null;
        for (const section of Object.values(CCM_RESOURCES)) {
          foundEntity = section.find(e => e.id === entityId);
          if (foundEntity) break;
        }

        if (!foundEntity) {
          setError('Entity not found');
          return;
        }

        // Get player card data
        const cardData = PLAYER_CARDS[entityId] || {
          ...PLAYER_CARDS['default'],
          snapshot: {
            ...PLAYER_CARDS['default'].snapshot,
            name: foundEntity.name,
            title: foundEntity.title,
            organization: foundEntity.organization,
          },
          entityType: foundEntity.entityType,
        };

        setPlayerCardData(cardData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadPlayerCard();
  }, [entityId]);

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    setShowCopiedSnackbar(true);
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !playerCardData) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: BRAND_COLORS.primary,
          p: 4,
        }}
      >
        <Typography
          variant="overline"
          sx={{
            fontSize: '1rem',
            color: 'rgba(255,255,255,0.6)',
            letterSpacing: '0.12em',
            mb: 2,
          }}
        >
          faana
        </Typography>
        <Alert severity="error" sx={{ maxWidth: 400 }}>
          {error || 'Player card not found'}
        </Alert>
      </Box>
    );
  }

  const { snapshot, vision, company, highlights, entityType } = playerCardData;
  const EntityIcon = ENTITY_ICONS[entityType] || PersonIcon;
  const entityConfig = ENTITY_TYPE_CONFIG[entityType] || ENTITY_TYPE_CONFIG.person;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: BRAND_COLORS.primary }}>
      {/* Floating action buttons - top right */}
      <Box
        sx={{
          position: 'fixed',
          top: 16,
          right: 16,
          zIndex: 100,
          display: 'flex',
          gap: 1,
        }}
        className="no-print"
      >
        <Tooltip title="Copy share link">
          <IconButton
            onClick={handleShare}
            sx={{
              bgcolor: 'rgba(255,255,255,0.15)',
              color: 'white',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
            }}
          >
            <ShareIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Print">
          <IconButton
            onClick={handlePrint}
            sx={{
              bgcolor: 'rgba(255,255,255,0.15)',
              color: 'white',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
            }}
          >
            <PrintIcon />
          </IconButton>
        </Tooltip>
      </Box>
      {/* Player Card Content */}
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper
          elevation={3}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              bgcolor: BRAND_COLORS.primary,
              color: 'white',
              p: 3,
            }}
          >
            <Chip
              icon={<EntityIcon sx={{ color: 'white !important', fontSize: 14 }} />}
              label={entityConfig.label}
              size="small"
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                color: 'white',
                mb: 1,
              }}
            />

            <Typography
              variant="overline"
              sx={{
                fontSize: '0.75rem',
                color: 'rgba(255,255,255,0.6)',
                letterSpacing: '0.12em',
                mb: 0.5,
              }}
            >
              faana
            </Typography>

            <Typography variant="h4" sx={{ fontWeight: 700, color: 'white' }}>
              {entityType === 'person' ? 'Founder' : entityConfig.label}
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 700, color: BRAND_COLORS.secondary }}>
              Snapshot
            </Typography>
          </Box>

          {/* Tab Navigation */}
          <Tabs
            value={currentTab}
            onChange={(e, v) => setCurrentTab(v)}
            variant="fullWidth"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 },
              '& .Mui-selected': { color: BRAND_COLORS.secondary },
              '& .MuiTabs-indicator': { bgcolor: BRAND_COLORS.secondary },
            }}
            className="no-print"
          >
            <Tab label="Snapshot" />
            <Tab label="Vision" />
            <Tab label="Company" />
            <Tab label="Highlights" />
          </Tabs>

          {/* Content */}
          <Box sx={{ bgcolor: BRAND_COLORS.primary }}>
            {/* Page 1: Snapshot */}
            <TabPanel value={currentTab} index={0}>
              <Box sx={{ p: 4, color: 'white' }}>
                <Box sx={{ display: 'flex', gap: 4, mb: 4 }}>
                  <Avatar
                    src={snapshot.photoUrl}
                    sx={{
                      width: 150,
                      height: 150,
                      bgcolor: BRAND_COLORS.secondary,
                      fontSize: '3rem',
                      fontWeight: 700,
                      border: '4px solid',
                      borderColor: BRAND_COLORS.secondary,
                    }}
                  >
                    {getInitials(snapshot.name)}
                  </Avatar>
                  <Box sx={{ pt: 2 }}>
                    <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, color: 'white' }}>
                      {snapshot.name}
                    </Typography>
                    <Typography variant="h5" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                      {snapshot.title}
                      {snapshot.organization && `, ${snapshot.organization}`}
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    bgcolor: BRAND_COLORS.secondary,
                    borderRadius: 4,
                    px: 4,
                    py: 1.5,
                    display: 'inline-block',
                    mb: 3,
                  }}
                >
                  <Typography variant="h5" sx={{ fontWeight: 600, color: 'white' }}>
                    Quick Bio:
                  </Typography>
                </Box>

                <Typography variant="h6" sx={{ lineHeight: 1.8, color: 'rgba(255,255,255,0.9)' }}>
                  {snapshot.quickBio}
                </Typography>
              </Box>
            </TabPanel>

            {/* Page 2: Vision */}
            <TabPanel value={currentTab} index={1} sx={{ minHeight: 400 }}>
              <Box sx={{ p: 4, color: 'white' }}>
                <Grid container spacing={4}>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6
                    }}>
                    <Typography variant="h6" sx={{ color: BRAND_COLORS.secondary, fontWeight: 700, mb: 2 }}>
                      Vision of a Better Place
                    </Typography>
                    <Box sx={{ bgcolor: 'white', borderRadius: 2, p: 3, minHeight: 120 }}>
                      <Typography sx={{ color: '#1a1a1a !important' }}>{vision.betterPlace || 'Not yet defined'}</Typography>
                    </Box>
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6
                    }}>
                    <Typography variant="h6" sx={{ color: BRAND_COLORS.secondary, fontWeight: 700, mb: 2 }}>
                      Purpose & Point of the Company
                    </Typography>
                    <Box sx={{ bgcolor: 'white', borderRadius: 2, p: 3, minHeight: 120 }}>
                      <Typography sx={{ color: '#1a1a1a !important' }}>{vision.purpose || 'Not yet defined'}</Typography>
                    </Box>
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6
                    }}>
                    <Typography variant="h6" sx={{ color: BRAND_COLORS.secondary, fontWeight: 700, mb: 2 }}>
                      What You Aim to Change in the World
                    </Typography>
                    <Box sx={{ bgcolor: 'white', borderRadius: 2, p: 3, minHeight: 120 }}>
                      <Typography sx={{ color: '#1a1a1a !important' }}>{vision.aimToChange || 'Not yet defined'}</Typography>
                    </Box>
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6
                    }}>
                    <Typography variant="h6" sx={{ color: BRAND_COLORS.secondary, fontWeight: 700, mb: 2 }}>
                      Unique Approach
                    </Typography>
                    <Box sx={{ bgcolor: 'white', borderRadius: 2, p: 3, minHeight: 120 }}>
                      <Typography sx={{ color: '#1a1a1a !important' }}>{vision.uniqueApproach || 'Not yet defined'}</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </TabPanel>

            {/* Page 3: Company */}
            <TabPanel value={currentTab} index={2}>
              <Box sx={{ p: 4, color: 'white' }}>
                <Box sx={{ display: 'flex', gap: 4, mb: 4, flexWrap: 'wrap' }}>
                  <Typography variant="h2" sx={{ fontWeight: 700, color: 'white' }}>
                    Company
                  </Typography>
                  <Grid container spacing={2} sx={{ flex: 1, minWidth: 300 }}>
                    {[
                      ['Company Name', company.name],
                      ['Tagline', company.tagline],
                      ['Industry', company.industry],
                      ['Founded', company.founded],
                      ['Headquarters', company.headquarters],
                      ['Stage', company.stage],
                    ].map(([label, value]) => (
                      <Grid key={label} size={6}>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>{label}:</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: 'white' }}>{value || '-'}</Typography>
                      </Grid>
                    ))}
                  </Grid>
                </Box>

                <Typography variant="h3" sx={{ fontWeight: 700, mb: 3, color: 'white' }}>
                  Overview
                </Typography>

                <Box sx={{ mb: 4 }}>
                  <Typography sx={{ color: 'rgba(255,255,255,0.7)', mb: 1 }}>Core Offering / Product:</Typography>
                  <Box sx={{ bgcolor: 'white', borderRadius: 2, p: 3 }}>
                    <Typography sx={{ color: '#1a1a1a !important' }}>{company.coreOffering || 'Not yet defined'}</Typography>
                  </Box>
                </Box>

                <Box>
                  <Typography sx={{ color: 'rgba(255,255,255,0.7)', mb: 1 }}>Target Customer / Market:</Typography>
                  <Box sx={{ bgcolor: 'white', borderRadius: 2, p: 3 }}>
                    <Typography sx={{ color: '#1a1a1a !important' }}>{company.targetMarket || 'Not yet defined'}</Typography>
                  </Box>
                </Box>
              </Box>
            </TabPanel>

            {/* Page 4: Highlights */}
            <TabPanel value={currentTab} index={3}>
              <Box sx={{ p: 4, color: 'white' }}>
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 4, textAlign: 'center', color: 'white' }}>
                  Company Highlights
                </Typography>

                <Grid container spacing={3} sx={{ mb: 5 }}>
                  {[
                    ['Revenue', highlights.revenue],
                    ['Customers', highlights.customers],
                    ['Partnerships', highlights.partnerships],
                    ['Milestones', highlights.milestones],
                    ['Awards', highlights.awards],
                  ].map(([label, value]) => (
                    <Grid
                      key={label}
                      size={{
                        xs: 6,
                        sm: 2.4
                      }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: 'white' }}>{value}</Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>{label}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                <Grid container spacing={2} sx={{ mb: 4 }}>
                  {Object.entries(highlights.metrics).map(([key, value]) => (
                    <Grid
                      key={key}
                      size={{
                        xs: 6,
                        sm: 4,
                        md: 2.4
                      }}>
                      <Box sx={{ bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2, p: 2, textAlign: 'center' }}>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                          {key.replace(/([A-Z])/g, ' $1').trim()}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: 'white' }}>{value}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, textAlign: 'center', color: 'white' }}>
                  Funding Snapshot
                </Typography>

                <Grid container spacing={2}>
                  {Object.entries(highlights.funding).map(([key, value]) => (
                    <Grid
                      key={key}
                      size={{
                        xs: 12,
                        sm: 4
                      }}>
                      <Box sx={{ bgcolor: BRAND_COLORS.secondary, borderRadius: '12px 12px 0 0', px: 2, py: 0.5 }}>
                        <Typography variant="caption" sx={{ color: 'white', fontWeight: 600 }}>
                          {key.replace(/([A-Z])/g, ' $1').trim()}
                        </Typography>
                      </Box>
                      <Box sx={{ bgcolor: 'rgba(255,255,255,0.1)', borderRadius: '0 0 12px 12px', p: 2, minHeight: 80 }}>
                        <Typography sx={{ color: 'white' }}>{value}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </TabPanel>
          </Box>

          {/* Footer */}
          <Box
            sx={{
              bgcolor: BRAND_COLORS.secondary,
              py: 1.5,
              px: 3,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography
              sx={{
                color: 'white',
                fontWeight: 500,
                letterSpacing: '0.05em',
              }}
            >
              faanaworks.com
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }} className="no-print">
              {[0, 1, 2, 3].map((i) => (
                <Box
                  key={i}
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: currentTab === i ? 'white' : 'rgba(255,255,255,0.4)',
                    cursor: 'pointer',
                  }}
                  onClick={() => setCurrentTab(i)}
                />
              ))}
            </Box>
          </Box>
        </Paper>
      </Container>
      {/* Copy confirmation snackbar */}
      <Snackbar
        open={showCopiedSnackbar}
        autoHideDuration={2000}
        onClose={() => setShowCopiedSnackbar(false)}
        message="Link copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
      {/* Print styles */}
      <style>
        {`
          @media print {
            .no-print {
              display: none !important;
            }
          }
        `}
      </style>
    </Box>
  );
}

export default PlayerCardPage;
